import React, { useState, useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import type { SystemHealthData } from '../types';
import { HealthHeatmap } from '../components/HealthHeatmap';
import { apiRequest } from '../config/api';
import { useTheme } from '../hooks/useTheme';
import { useProject } from '../context/ProjectContext';
import { listRegistryTypes } from '../api/metaRegistriesApi';
import { getHealthColor } from '../types/systemHealth';
import Header from '../components/Header';
import NavigationRail, { NAV_RAIL_COLLAPSED_WIDTH } from '../components/navigation/NavigationRail';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

const SLUG_ICONS: Record<string, React.ReactNode> = {
  information_systems: <DnsOutlinedIcon fontSize="small" />,
  'business-capabilities': <ExtensionOutlinedIcon fontSize="small" />,
  'bcm-maps': <MapOutlinedIcon fontSize="small" />,
};
const FALLBACK_ICONS: React.ReactNode[] = [
  <CategoryOutlinedIcon fontSize="small" />,
  <LayersOutlinedIcon fontSize="small" />,
  <HubOutlinedIcon fontSize="small" />,
  <SchemaOutlinedIcon fontSize="small" />,
  <ListAltOutlinedIcon fontSize="small" />,
  <WidgetsOutlinedIcon fontSize="small" />,
  <AssignmentOutlinedIcon fontSize="small" />,
  <AccountTreeOutlinedIcon fontSize="small" />,
];
const getRegistryIcon = (s: string, i = 0): React.ReactNode =>
  SLUG_ICONS[s] ?? FALLBACK_ICONS[i % FALLBACK_ICONS.length];

const SystemHealthHeatmapPage: React.FC = () => {
  const [healthData, setHealthData] = useState<SystemHealthData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSystem, setSelectedSystem] = useState<SystemHealthData | null>(null);
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { theme } = useTheme();
  const { currentProject } = useProject();
  const [railWidth, setRailWidth] = useState(NAV_RAIL_COLLAPSED_WIDTH);
  const [allRegistryTypes, setAllRegistryTypes] = useState<{ slug: string; name: string }[]>([]);

  useEffect(() => {
    loadHealthData();
  }, []);

  const loadHealthData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await (apiRequest as any)('/systems/k8s-mapping');
      setHealthData(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Failed to load health data:', err);
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) listRegistryTypes(projectId).then(setAllRegistryTypes).catch(() => {});
  }, [projectId]);

  const railItems = useMemo(() => {
    const dedicated: Record<string, string> = { information_systems: 'systems' };
    const seen = new Set<string>();
    const role = currentProject?.role;
    const items = [
      {
        id: 'diagrams', label: 'Диаграммы',
        icon: <GridViewOutlinedIcon fontSize="small" />, active: false,
        onClick: () => navigate(`/projects/${encodeURIComponent(projectId ?? '')}`),
      },
      ...allRegistryTypes
        .filter((rt) => { if (seen.has(rt.slug)) return false; seen.add(rt.slug); return true; })
        .map((rt, idx) => {
          const path = rt.slug === 'bcm-maps'
            ? `/projects/${encodeURIComponent(projectId!)}/bcm`
            : dedicated[rt.slug]
            ? `/projects/${encodeURIComponent(projectId!)}/${dedicated[rt.slug]}`
            : `/projects/${encodeURIComponent(projectId!)}/registry/${encodeURIComponent(rt.slug)}`;
          return {
            id: rt.slug, label: rt.name, icon: getRegistryIcon(rt.slug, idx),
            active: false,
            onClick: () => navigate(path),
          };
        }),
      {
        id: 'health-map', label: 'Карта работоспособности',
        icon: <MonitorHeartOutlinedIcon fontSize="small" />, active: true,
        onClick: () => {},
      },
    ];
    if (role === 'owner' || role === 'admin') {
      items.push({
        id: 'settings', label: 'Настройки пространства',
        icon: <SettingsOutlinedIcon fontSize="small" />, active: false,
        onClick: () => navigate(`/projects/${encodeURIComponent(projectId!)}/settings`),
      });
    }
    return items;
  }, [allRegistryTypes, projectId, navigate, currentProject?.role]);

  const handleBack = () => {
    navigate(`/projects/${encodeURIComponent(projectId ?? '')}`);
  };

  const handleSystemClick = (system: SystemHealthData) => {
    setSelectedSystem(system);
  };

  const calculateStats = () => {
    const total = healthData.length;
    const excellent = healthData.filter(s => s.health_percent >= 100).length;
    const good = healthData.filter(s => s.health_percent >= 80 && s.health_percent < 100).length;
    const warning = healthData.filter(s => s.health_percent >= 50 && s.health_percent < 80).length;
    const critical = healthData.filter(s => s.health_percent > 0 && s.health_percent < 50).length;
    const unknown = healthData.filter(s => s.health_percent === 0).length;
    const avgHealth = total > 0 ? Math.round(healthData.reduce((sum, s) => sum + s.health_percent, 0) / total) : 0;

    return { total, excellent, good, warning, critical, unknown, avgHealth };
  };

  const stats = calculateStats();
  const isDark = theme.name === 'dark';
  const pageBackground = isDark
    ? 'radial-gradient(circle at top left, rgba(37, 99, 235, 0.18), transparent 34%), #0f1419'
    : 'radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 32%), #f5f7fa';
  const surface = isDark ? '#151b23' : '#ffffff';
  const surfaceSoft = isDark ? '#111827' : '#f8fafc';
  const border = isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.28)';
  const mutedText = isDark ? '#94a3b8' : '#64748b';
  const primaryActionSx = {
    borderRadius: '8px',
    border: '1px solid #2563eb',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    fontWeight: 700,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#1d4ed8',
      borderColor: '#1d4ed8',
      boxShadow: '0 10px 24px rgba(37, 99, 235, 0.28)',
    },
  };
  const secondaryActionSx = {
    borderRadius: '8px',
    borderColor: border,
    color: theme.toolbar.buttonText,
    textTransform: 'none',
    '&:hover': {
      borderColor: '#2563eb',
      backgroundColor: isDark ? 'rgba(37, 99, 235, 0.12)' : 'rgba(37, 99, 235, 0.06)',
    },
  };
  const cardSx = {
    borderRadius: '8px',
    border: `1px solid ${border}`,
    background: surface,
    boxShadow: 'none',
  };
  const statTiles = [
    { label: 'Всего систем', value: stats.total, suffix: '', color: '#2563eb' },
    { label: 'Средний уровень', value: stats.avgHealth, suffix: '%', color: stats.avgHealth >= 80 ? '#16a34a' : stats.avgHealth >= 50 ? '#d97706' : '#dc2626' },
    { label: 'Отлично', value: stats.excellent, suffix: '', color: '#16a34a' },
    { label: 'Хорошо', value: stats.good, suffix: '', color: '#65a30d' },
    { label: 'Предупреждение', value: stats.warning, suffix: '', color: '#d97706' },
    { label: 'Критично', value: stats.critical, suffix: '', color: '#dc2626' },
    { label: 'Нет данных', value: stats.unknown, suffix: '', color: mutedText },
  ];
  const selectedHealthColor = selectedSystem ? getHealthColor(selectedSystem.health_percent) : '#94a3b8';

  const renderValueList = (items: Array<string | { id?: string; cluster_id?: string; name: string }> = []) => (
    <Stack spacing={0.75} sx={{ mt: 1 }}>
      {items.map((item) => {
        const name = typeof item === 'string' ? item : item.name;
        const key = typeof item === 'string' ? item : (item.id || `${item.cluster_id || 'item'}/${item.name}`);
        return (
          <Chip
            key={key}
            label={name}
            size="small"
            sx={{
              alignSelf: 'flex-start',
              borderRadius: '8px',
              backgroundColor: surfaceSoft,
              color: theme.toolbar.buttonText,
              border: `1px solid ${border}`,
            }}
          />
        );
      })}
    </Stack>
  );

  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
      <Typography sx={{ color: mutedText, fontSize: 13 }}>{label}</Typography>
      <Typography sx={{ color: theme.toolbar.buttonText, fontSize: 13, fontWeight: 700, textAlign: 'right' }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <>
      <Header title="Карта работоспособности" height={56} />
      <NavigationRail
        ariaLabel="Реестры пространства"
        theme={theme}
        items={railItems}
        onExpandedChange={setRailWidth}
      />
      <Box
        component="main"
        sx={{
          px: { xs: 2, md: 3 },
          pt: '88px',
          pb: 4,
          pl: `${railWidth + 24}px`,
          background: pageBackground,
          minHeight: '100vh',
          transition: 'padding-left .16s ease',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
          <Paper sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      display: 'grid',
                      placeItems: 'center',
                      color: '#2563eb',
                      backgroundColor: isDark ? 'rgba(37, 99, 235, 0.18)' : 'rgba(37, 99, 235, 0.1)',
                    }}
                  >
                    <MonitorHeartOutlinedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="h4" component="h1" sx={{ color: theme.toolbar.text, fontWeight: 800, letterSpacing: 0, fontSize: { xs: 26, md: 30 } }}>
                      Тепловая карта работоспособности
                    </Typography>
                    <Typography sx={{ color: mutedText, mt: 0.25, fontSize: 14 }}>
                      Реальное состояние систем на основе данных Kubernetes.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack} sx={secondaryActionSx}>
                  Назад
                </Button>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshOutlinedIcon />}
                  onClick={loadHealthData}
                  disabled={loading}
                  sx={primaryActionSx}
                >
                  Обновить
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 1.5, mb: 2.5 }}>
            {statTiles.map((tile) => (
              <Paper
                key={tile.label}
                sx={{
                  ...cardSx,
                  p: 2,
                  minHeight: 104,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'transform .16s ease, border-color .16s ease, box-shadow .16s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    borderColor: tile.color,
                    boxShadow: isDark ? '0 16px 34px rgba(0,0,0,0.22)' : '0 16px 34px rgba(15,23,42,0.08)',
                  },
                }}
              >
                <Typography sx={{ color: mutedText, fontSize: 13, fontWeight: 700 }}>{tile.label}</Typography>
                <Typography sx={{ color: tile.color, fontSize: 32, lineHeight: 1, fontWeight: 850 }}>
                  {tile.value}{tile.suffix}
                </Typography>
              </Paper>
            ))}
          </Box>

          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2.5, borderRadius: '8px' }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: selectedSystem ? 'minmax(0, 1fr) 380px' : '1fr' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <Paper sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, minHeight: 420 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2 }}>
                <Box>
                  <Typography sx={{ color: theme.toolbar.text, fontWeight: 800, fontSize: 18 }}>Карта систем</Typography>
                  <Typography sx={{ color: mutedText, fontSize: 13, mt: 0.25 }}>
                    Нажмите на плитку, чтобы открыть подробности.
                  </Typography>
                </Box>
                <Chip
                  label={`${healthData.length} систем`}
                  size="small"
                  sx={{ borderRadius: '8px', backgroundColor: surfaceSoft, color: theme.toolbar.buttonText, border: `1px solid ${border}` }}
                />
              </Stack>

              {loading ? (
                <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 300, color: mutedText }}>
                  <Stack spacing={1.5} alignItems="center">
                    <CircularProgress size={28} />
                    <Typography>Загрузка данных Kubernetes...</Typography>
                  </Stack>
                </Box>
              ) : healthData.length > 0 ? (
                <HealthHeatmap data={healthData} onSystemClick={handleSystemClick} />
              ) : (
                <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 300, textAlign: 'center', color: mutedText }}>
                  <Box>
                    <InfoOutlinedIcon sx={{ fontSize: 48, color: isDark ? '#475569' : '#cbd5e1' }} />
                    <Typography sx={{ mt: 1.5 }}>Нет данных для отображения</Typography>
                  </Box>
                </Box>
              )}
            </Paper>

            {selectedSystem && (
              <Paper sx={{ ...cardSx, p: 2.5, position: { lg: 'sticky' }, top: 80 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: theme.toolbar.text, fontWeight: 800, fontSize: 18, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {selectedSystem.system_name}
                    </Typography>
                    <Typography sx={{ color: mutedText, fontSize: 13, mt: 0.25 }}>
                      {selectedSystem.system_code}
                    </Typography>
                  </Box>
                  <Button size="small" variant="outlined" onClick={() => setSelectedSystem(null)} sx={secondaryActionSx}>
                    Закрыть
                  </Button>
                </Stack>

                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                    <Typography sx={{ color: mutedText, fontSize: 13, fontWeight: 700 }}>Работоспособность</Typography>
                    <Typography sx={{ color: selectedHealthColor, fontWeight: 850 }}>{selectedSystem.health_percent}%</Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.max(0, Math.min(100, selectedSystem.health_percent))}
                    sx={{
                      height: 8,
                      borderRadius: '8px',
                      backgroundColor: surfaceSoft,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: selectedHealthColor,
                        borderRadius: '8px',
                      },
                    }}
                  />
                </Box>

                <Divider sx={{ borderColor: border, my: 1.5 }} />
                <DetailRow label="Тип" value={selectedSystem.system_type} />
                <DetailRow label="Статус" value={selectedSystem.status} />
                <DetailRow label="Всего подов" value={selectedSystem.total_pods} />
                <DetailRow label="Запущено" value={selectedSystem.running_pods} />
                <DetailRow label="Ожидание" value={selectedSystem.pending_pods} />
                <DetailRow label="Ошибки" value={selectedSystem.failed_pods} />

                {selectedSystem.clusters && selectedSystem.clusters.length > 0 && (
                  <>
                    <Divider sx={{ borderColor: border, my: 1.5 }} />
                    <Typography sx={{ color: theme.toolbar.text, fontWeight: 800, fontSize: 14 }}>Кластеры</Typography>
                    {renderValueList(selectedSystem.clusters)}
                  </>
                )}

                {selectedSystem.namespaces && selectedSystem.namespaces.length > 0 && (
                  <>
                    <Divider sx={{ borderColor: border, my: 1.5 }} />
                    <Typography sx={{ color: theme.toolbar.text, fontWeight: 800, fontSize: 14 }}>Namespaces</Typography>
                    {renderValueList(selectedSystem.namespaces)}
                  </>
                )}

                {selectedSystem.modules && selectedSystem.modules.length > 0 && (
                  <>
                    <Divider sx={{ borderColor: border, my: 1.5 }} />
                    <Typography sx={{ color: theme.toolbar.text, fontWeight: 800, fontSize: 14, mb: 1 }}>
                      Модули ({selectedSystem.modules.length})
                    </Typography>
                    <Stack spacing={1}>
                      {selectedSystem.modules.map((module) => (
                        <Box key={module.module_id} sx={{ border: `1px solid ${border}`, borderRadius: '8px', p: 1.25, backgroundColor: surfaceSoft }}>
                          <Typography sx={{ color: theme.toolbar.buttonText, fontWeight: 700, fontSize: 13 }}>
                            {module.module_name}
                          </Typography>
                          <Typography sx={{ color: mutedText, fontSize: 12, mt: 0.25 }}>
                            {module.running_pods}/{module.total_pods} pods - {module.health_percent}%
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </>
                )}

                {selectedSystem.last_sync_time && (
                  <>
                    <Divider sx={{ borderColor: border, my: 1.5 }} />
                    <Typography sx={{ color: mutedText, fontSize: 12 }}>
                      Последняя синхронизация: {new Date(selectedSystem.last_sync_time).toLocaleString()}
                    </Typography>
                  </>
                )}
              </Paper>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SystemHealthHeatmapPage;
